import React from 'react'
import { useTranslation } from '../hooks/use-i18n'

export interface PlacePredictionRow {
  playerName: string
  driver: string
}

interface Formula1PlacePredictionsModalProps {
  isOpen: boolean
  position: number
  predictions: PlacePredictionRow[]
  onClose: () => void
}

const positionSuffix = (position: number) => {
  if (position === 1) return 'st'
  if (position === 2) return 'nd'
  if (position === 3) return 'rd'
  return 'th'
}

const Formula1PlacePredictionsModal: React.FC<Formula1PlacePredictionsModalProps> = ({
  isOpen,
  position,
  predictions,
  onClose,
}) => {
  const { t } = useTranslation()
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  if (!isOpen) return null

  const positionLabel = `${position}${positionSuffix(position)}`
  const title = t('formula1.predictionsForPlace', { position: positionLabel })

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col glass-strong rounded-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-white/10 bg-white/5">
          <h2 className="text-base sm:text-lg font-bold text-foreground">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t('stats.close')}
          >
            <span className="text-xl leading-none">×</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5">
          {predictions.length === 0 ? (
            <p className="text-center text-muted-foreground text-sm">
              {t('formula1.noPredictionsForPlace')}
            </p>
          ) : (
            <ul className="space-y-2">
              {predictions.map((row, index) => (
                <li
                  key={`${row.playerName}-${row.driver}-${index}`}
                  className="rounded-lg p-3 border border-border/50 bg-card flex items-center justify-between gap-2"
                >
                  <span className="font-semibold text-foreground truncate">
                    {row.playerName}
                  </span>
                  <span className="text-primary font-medium shrink-0">
                    {row.driver}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-4 sm:p-5 border-t border-white/10 bg-white/5">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
          >
            {t('stats.close')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Formula1PlacePredictionsModal
